import { SmilePlus } from 'lucide-react';
import { groupReactions, QUICK_REACTIONS, type MessageReaction } from '../../../../lib/message-reactions';
import { Button, IconButton } from '../../ui';
import { useTranslation } from '../../../../lib/i18n';
import styles from './MessageReactions.module.css';

// Reaction chips — one pill per distinct emoji, count when >1. Clicking any chip toggles that emoji;
// chips stay clickable on locked (WhatsApp/SMS-owned) threads too, because reactions are in-app
// annotations, not channel sends.
//
// The pill is the shape BOTH phones already draw (iOS `Capsule()` + accent stroke on the user's own,
// Android `RoundedCornerShape(999.dp)` + accent ring and tint), so web was the odd surface out. It
// stays a DS `<Button>` — the skin is what is overridden, per the suggested-task-chips precedent:
// the DS has no round shape and no reaction-chip variant, and the repo's "no pills" rule is about
// drift, not about a control the other two surfaces specify as round.
export function ReactionChips({
  reactions,
  onToggle,
  inset = 0,
  overlapBubble = false,
}: {
  reactions: MessageReaction[] | undefined;
  onToggle?: (emoji: string) => void;
  // The row's reading-column inset, when it has one. The row shrink-wraps its chips, so padding on
  // BOTH sides insets them off whichever edge they are aligned to — a user row's end, an assistant
  // row's start — landing them in the same column as the bubble they belong to.
  inset?: number;
  // This row DRAWS a bubble, so the chips straddle its bottom edge the way WhatsApp's do, tucked in
  // from the corner. Only a drawn bubble has an edge to sit on: assistant prose flows bare on the
  // surface, where the same pull-up would land the pill on the last line of the reply.
  overlapBubble?: boolean;
}) {
  const groups = groupReactions(reactions);
  if (groups.length === 0) return null;
  return (
    <div
      style={{
        ...CHIPS_ROW,
        ...(inset ? { paddingInlineStart: inset, paddingInlineEnd: inset } : null),
        ...(overlapBubble ? OVERLAP_BUBBLE : null),
      }}
    >
      {groups.map((g) => {
        const who = g.who.join(', ');
        return (
          <Button
            key={g.emoji}
            variant="outlined"
            color="standard"
            size="tiny"
            onClick={onToggle ? () => onToggle(g.emoji) : undefined}
            aria-pressed={g.mine}
            aria-label={`${g.emoji} ${who}`}
            title={who}
            className={g.mine ? `${styles.chip} ${styles.mine}` : styles.chip}
          >
            <span aria-hidden>{g.emoji}</span>
            {g.count > 1 && <span style={CHIP_COUNT}>{g.count}</span>}
          </Button>
        );
      })}
    </div>
  );
}

// The smiley toggle in the tool bar (Figma 14511:160345 "When Click on Emoji icon", glyph per
// 14623:185150 — a smiley WITH a plus badge, i.e. lucide's SmilePlus, not the bare Smile): the DS
// Symphony Icon Button; clicking it opens the quick-react bar below and holds the ghost/hover
// fill while open. Always visible, like the rest of the tool bar.
export function ReactToggleButton({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const { t } = useTranslation();
  return (
    <IconButton
      size="small"
      variant="text"
      onClick={onToggle}
      aria-label={t('chat.reactions.react')}
      aria-expanded={open}
      title={t('chat.reactions.react')}
      style={open ? { background: 'var(--ds-surface-light-gray)' } : undefined}
    >
      <SmilePlus size={16} strokeWidth={1.5} absoluteStrokeWidth aria-hidden />
    </IconButton>
  );
}

// Quick-react bar — the emoji row the smiley reveals (Figma emoji bar: 30px ghost icon buttons,
// gap 2). Picking one toggles that reaction; the parent closes the bar.
export function QuickReactBar({ onToggle }: { onToggle: (emoji: string) => void }) {
  const { t } = useTranslation();
  return (
    <div role="group" aria-label={t('chat.reactions.react')} style={QUICK_BAR}>
      {QUICK_REACTIONS.map((emoji) => (
        <IconButton key={emoji} size="small" variant="text" onClick={() => onToggle(emoji)} aria-label={emoji} title={emoji}>
          <span aria-hidden style={{ fontSize: '1rem', lineHeight: 1 }}>
            {emoji}
          </span>
        </IconButton>
      ))}
    </div>
  );
}

const CHIPS_ROW: React.CSSProperties = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: 4,
  marginTop: 4,
};

// How far the pill rides up over the bubble it belongs to, out of the DS tiny button's 26px. Enough
// that it reads as attached to the bubble rather than parked under it, and no more — a deeper bite
// crowds the emoji against the edge it straddles.
const BUBBLE_OVERLAP = 6;

// Tucked in from the bubble's corner as well as up over its edge (WhatsApp sets the pill in rather
// than hanging it off the corner). Logical, so RTL tucks it off the other side for free.
const OVERLAP_BUBBLE: React.CSSProperties = {
  marginTop: -BUBBLE_OVERLAP,
  marginInlineStart: 12,
  marginInlineEnd: 12,
};

const CHIP_COUNT: React.CSSProperties = {
  marginInlineStart: 4,
  fontSize: '0.71875rem',
  fontWeight: 'var(--sym-weight-semibold)',
  color: 'currentColor',
  opacity: 0.7,
};

const QUICK_BAR: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 2,
};
